import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCcw,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  XCircle,
  Clock,
  CandlestickChart,
  Download,
  Calendar,
} from "lucide-react";
import Logo from "@/components/marketing/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
  { key: "open", label: "Open" },
];

const StatusBadge = ({ status }) => {
  if (status === "won") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-mono bg-vts-profit/15 text-vts-profit border border-vts-profit/30">
        <CheckCircle2 className="h-3 w-3" /> Won
      </span>
    );
  }
  if (status === "lost") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-mono bg-vts-loss/15 text-vts-loss border border-vts-loss/30">
        <XCircle className="h-3 w-3" /> Lost
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-mono bg-vts-surface text-vts-silver/70 border border-vts-line">
      <Clock className="h-3 w-3" /> Open
    </span>
  );
};

const DirectionIcon = ({ contractType }) => {
  const t = (contractType || "").toUpperCase();
  if (t === "CALL" || t === "DIGITEVEN")
    return <TrendingUp className="h-3.5 w-3.5 text-vts-profit" />;
  if (t === "PUT" || t === "DIGITODD")
    return <TrendingDown className="h-3.5 w-3.5 text-vts-loss" />;
  return <CandlestickChart className="h-3.5 w-3.5 text-vts-silver/60" />;
};

const contractLabel = (t) => {
  const u = (t || "").toUpperCase();
  if (u === "CALL") return "Rise";
  if (u === "PUT") return "Fall";
  if (u === "DIGITEVEN") return "Even";
  if (u === "DIGITODD") return "Odd";
  return u || "—";
};

const TradeHistory = () => {
  const navigate = useNavigate();
  const { session, isAuthed } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  // v8.9.115 — strategy-family filter dropdown. Lets the user drill into
  // Auto-FX / Auto-Trade / Hunter / a specific strategy by name.
  const [strategyFilter, setStrategyFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    if (!isAuthed) navigate("/");
  }, [isAuthed, navigate]);

  const accountId = session?.account_id;
  const PAGE_SIZE = 50;
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [serverSummary, setServerSummary] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchTrades = useCallback(
    async (opts = {}) => {
      if (!accountId) return;
      const nextSkip = opts.append ? skip + PAGE_SIZE : 0;
      setLoading(true);
      try {
        const params = {
          account_id: accountId,
          limit: PAGE_SIZE,
          skip: nextSkip,
        };
        if (filter !== "all") params.status = filter;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const res = await api.get("/track/trades", { params });
        const incoming = res.data?.trades || [];
        setTrades((prev) => (opts.append ? [...prev, ...incoming] : incoming));
        setTotal(res.data?.total || 0);
        setServerSummary(res.data?.summary || null);
        setHasMore(!!res.data?.has_more);
        setSkip(opts.append ? nextSkip : 0);
      } catch (e) {
        toast.error("Failed to load history", {
          description: e?.response?.data?.detail || e.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [accountId, filter, dateFrom, dateTo, skip]
  );

  // Re-fetch from page 0 whenever filters/dates change
  useEffect(() => {
    fetchTrades({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, filter, dateFrom, dateTo]);

  // Server already filters by status + date range; we just display & summarize.
  // v8.9.115 — Strategy family filter applied client-side (small dataset).
  const filtered = useMemo(() => {
    if (strategyFilter === "all") return trades;
    return trades.filter((t) => {
      const s = (t.strategy || "").toLowerCase();
      if (strategyFilter === "autofx") return s.startsWith("auto-fx");
      if (strategyFilter === "autotrade") return s.startsWith("auto-trade");
      if (strategyFilter === "hunter") return s.startsWith("digit hunter");
      // Exact strategy match (the dropdown lists each distinct strategy)
      return (t.strategy || "") === strategyFilter;
    });
  }, [trades, strategyFilter]);
  // Build the distinct-strategy list for the dropdown
  const distinctStrategies = useMemo(() => {
    const set = new Set();
    trades.forEach((t) => t.strategy && set.add(t.strategy));
    return Array.from(set).sort();
  }, [trades]);

  const summary = useMemo(() => {
    // KPIs must cover the WHOLE filtered range (server aggregate), not just
    // the 50 rows on screen. On-screen math only when the client-side
    // strategy filter narrows things down.
    if (serverSummary && strategyFilter === "all") {
      const settled = serverSummary.wins + serverSummary.losses;
      return {
        total,
        shown: filtered.length,
        wins: serverSummary.wins,
        losses: serverSummary.losses,
        pnl: serverSummary.pnl,
        volume: serverSummary.volume,
        winRate: settled ? (serverSummary.wins / settled) * 100 : 0,
      };
    }
    const won = filtered.filter((t) => t.status === "won");
    const lost = filtered.filter((t) => t.status === "lost");
    const settled = won.length + lost.length;
    const pnl = filtered.reduce((acc, t) => acc + (Number(t.profit) || 0), 0);
    const volume = filtered.reduce((acc, t) => acc + (Number(t.stake) || 0), 0);
    const winRate = settled ? (won.length / settled) * 100 : 0;
    return {
      total,
      shown: filtered.length,
      wins: won.length,
      losses: lost.length,
      pnl,
      volume,
      winRate,
    };
  }, [filtered, total, serverSummary, strategyFilter]);

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.warning("No trades to export");
      return;
    }
    const headers = [
      "Time",
      "Account",
      "Contract ID",
      "Market",
      "Contract Type",
      "Stake",
      "Payout",
      "Currency",
      "Status",
      "Profit",
      "Settled At",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = filtered.map((t) =>
      [
        t.created_at,
        t.account_id,
        t.contract_id,
        t.symbol,
        t.contract_type,
        t.stake,
        t.payout,
        t.currency,
        t.status,
        t.profit ?? "",
        t.settled_at ?? "",
      ]
        .map(escape)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `vts-trades-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} trades`);
  };

  const clearDates = () => {
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div data-testid="trade-history-page" className="min-h-screen bg-vts-bg">
      {/* Top bar */}
      <div className="border-b border-vts-line bg-black/60 backdrop-blur sticky top-0 z-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-vts-blue/15 text-vts-blue border border-vts-blue/30 font-mono">
              Trade History
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="history-back-to-desk"
              onClick={() => navigate("/trade")}
              className="text-xs text-vts-silver/70 hover:text-white px-3 py-2 flex items-center gap-1.5"
            >
              <ArrowLeft className="h-3 w-3" /> Back to desk
            </button>
            <Button
              data-testid="history-export-csv-btn"
              onClick={exportCsv}
              variant="outline"
              className="border-vts-line bg-vts-surface hover:bg-vts-surfaceHover text-white text-xs"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
            <Button
              data-testid="history-refresh-btn"
              onClick={fetchTrades}
              disabled={loading}
              className="bg-vts-blue hover:bg-vts-blueHover text-white text-xs"
            >
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-3xl text-white tracking-tight">
            Your trade history
          </h1>
          <p className="text-sm text-vts-silver/70 mt-1 font-body">
            Every contract you (and the bot) have executed on this account.
          </p>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="vts-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1">Trades</div>
            <div
              data-testid="history-total-trades"
              className="font-mono text-2xl text-white tabular-nums"
            >
              {summary.total}
            </div>
          </div>
          <div className="vts-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1">Wins</div>
            <div className="font-mono text-2xl text-vts-profit tabular-nums">{summary.wins}</div>
          </div>
          <div className="vts-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1">Losses</div>
            <div className="font-mono text-2xl text-vts-loss tabular-nums">{summary.losses}</div>
          </div>
          <div className="vts-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1">Win rate</div>
            <div className="font-mono text-2xl text-white tabular-nums">
              {summary.winRate.toFixed(0)}%
            </div>
          </div>
          <div className="vts-card p-4 border-vts-blue/40">
            <div className="text-[10px] uppercase tracking-wider text-vts-blue mb-1">Net P&L</div>
            <div
              data-testid="history-pnl"
              className={`font-mono text-2xl tabular-nums ${
                summary.pnl >= 0 ? "text-vts-profit" : "text-vts-loss"
              }`}
            >
              {summary.pnl >= 0 ? "+" : ""}
              {summary.pnl.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Filters + date range */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`history-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-wider border transition ${
                  filter === f.key
                    ? "bg-vts-blue/15 text-white border-vts-blue/40"
                    : "bg-vts-surface text-vts-silver/70 border-vts-line hover:bg-vts-surfaceHover"
                }`}
              >
                {f.label}
              </button>
            ))}
            {/* v8.9.115 — Strategy family dropdown */}
            <select
              data-testid="history-strategy-filter"
              value={strategyFilter}
              onChange={(e) => setStrategyFilter(e.target.value)}
              className="bg-vts-surface border border-vts-line rounded px-2 py-1.5 text-xs font-mono text-white uppercase tracking-wider"
              title="Filter by strategy family or by a specific strategy"
            >
              <option value="all">All strategies</option>
              <option value="autofx">Auto-FX only</option>
              <option value="autotrade">Auto-Trade only</option>
              <option value="hunter">Hunter only</option>
              {distinctStrategies.length > 0 && (
                <optgroup label="— Specific strategy —">
                  {distinctStrategies.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div className="flex items-center gap-2 lg:ml-auto flex-wrap">
            <Calendar className="h-3.5 w-3.5 text-vts-silver/60" />
            <Input
              data-testid="history-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-vts-surface border-vts-line text-white font-mono text-xs h-8 w-[150px]"
            />
            <span className="text-xs text-vts-silver/40 font-mono">→</span>
            <Input
              data-testid="history-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-vts-surface border-vts-line text-white font-mono text-xs h-8 w-[150px]"
            />
            {(dateFrom || dateTo) && (
              <button
                data-testid="history-date-clear"
                onClick={clearDates}
                className="text-[10px] uppercase tracking-wider text-vts-silver/60 hover:text-white px-2 font-mono"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="vts-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-vts-line bg-vts-bg/60">
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">Time</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">Market</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">Type</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">Stake</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">Payout</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">P&L</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">Status</th>
                </tr>
              </thead>
              <tbody data-testid="history-table-body" className="divide-y divide-vts-line/50">
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      data-testid="history-empty"
                      colSpan={7}
                      className="px-4 py-12 text-center text-vts-silver/60 text-sm"
                    >
                      {loading ? "Loading…" : "No trades to show. Place a contract from the trade desk to populate your history."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((t) => {
                    const profit = Number(t.profit);
                    const hasProfit = !Number.isNaN(profit) && t.profit != null;
                    return (
                      <tr
                        key={t.id || t.contract_id}
                        data-testid={`history-row-${t.contract_id || t.id}`}
                        className="hover:bg-vts-surface/40 transition"
                      >
                        <td className="px-4 py-3 text-xs text-vts-silver/70 font-mono whitespace-nowrap">
                          {t.created_at ? new Date(t.created_at).toLocaleString([], { hour12: false }) : "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-white font-mono">
                          {t.symbol}
                          {t.entry_win_rate != null && (
                            <span
                              data-testid={`history-why-${t.contract_id || t.id}`}
                              className="block text-[9px] text-vts-silver/45 mt-0.5"
                            >
                              {t.entry_rank === 0 ? "priority hunt" : t.entry_rank ? `#${t.entry_rank} ranked` : "ranked"} · {Number(t.entry_win_rate).toFixed(0)}% WR · {t.entry_trades || 0}n
                            </span>
                          )}
                          {t.secured_at != null && (
                            <span
                              data-testid={`history-secured-${t.contract_id || t.id}`}
                              className="block text-[9px] text-amber-300 mt-0.5"
                              title={`Auto-Secure sold this contract early at +${t.secured_at}% of its max payout`}
                            >
                              🔒 secured @ {t.secured_at}%
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-white">
                          <span className="inline-flex items-center gap-1.5">
                            <DirectionIcon contractType={t.contract_type} />
                            {contractLabel(t.contract_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-white font-mono text-right tabular-nums">
                          {Number(t.stake).toFixed(2)} {t.currency}
                        </td>
                        <td className="px-4 py-3 text-xs text-white font-mono text-right tabular-nums">
                          {t.payout == null || Number.isNaN(Number(t.payout)) ? "—" : `${Number(t.payout).toFixed(2)} ${t.currency || ""}`}
                        </td>
                        <td
                          className={`px-4 py-3 text-xs font-mono text-right tabular-nums ${
                            !hasProfit
                              ? "text-vts-silver/40"
                              : profit >= 0
                              ? "text-vts-profit"
                              : "text-vts-loss"
                          }`}
                        >
                          {!hasProfit ? "—" : `${profit >= 0 ? "+" : ""}${profit.toFixed(2)}`}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={t.status || "open"} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-[11px] text-vts-silver/40 mt-4 leading-relaxed">
          {total > 0 && (
            <span data-testid="history-pagination-summary" className="font-mono mr-2">
              Showing {filtered.length} of {total} trades.
            </span>
          )}
          History reflects contracts tracked by VTS during your sessions. Past performance is not indicative of future results.
        </p>

        {hasMore && (
          <div className="mt-6 flex justify-center">
            <Button
              data-testid="history-load-more-btn"
              onClick={() => fetchTrades({ append: true })}
              disabled={loading}
              variant="outline"
              className="border-vts-line bg-vts-surface hover:bg-vts-surfaceHover text-white"
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeHistory;
